import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnInit,
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { filter, tap } from 'rxjs/operators';
import { BehaviorSubject, catchError, of } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { CompanyService } from '@app/company/services/company.service';
import { ProfileService } from './services/profile.service';
import { ServiceService } from '@services/service/service.service';
import { AuthService } from '@services/security';

@UntilDestroy()
@Component({
    selector: 'app-profiles',
    templateUrl: './profiles-company.component.html',
    styleUrls: ['./profiles-company.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfilesCompanyComponent implements OnInit {
    profile;
    profiles = [];
    showNav = new BehaviorSubject<boolean>(null);
    company;

    constructor(
        private readonly translate: TranslateService,
        private readonly toastr: ToastrService,
        private router: Router,
        private profileService: ProfileService,
        private change: ChangeDetectorRef,
        private companyService: CompanyService,
        private route: ActivatedRoute,
        private serviceService: ServiceService,
        private authService: AuthService,
    ) {}

    ngOnInit(): void {
        this.profileService.currentNavigation.subscribe((data) => {
            this.showNav.next(data);
        });
        this.initCompany();
        this.profile = this.profileService.initProfile();
        this.getProfiles();
        this.onRouteChange();
    }

    toggleCreation(type) {
        this.showNav.next(true);
        this.profile.total = this.profiles.length;
        this.profile.type = type;
        this.profileService.labels.push(...this.getCompanyServices())
        this.router.navigate(['create', type, 'general'], {
            relativeTo: this.route,
        });
        this.profileService.setPreviousRoute(this.router.url);
    }


    initCompany() {
        if (this.authService.isAdmin) {
            this.companyService.company$.subscribe((company) => {
                if (company) {
                    this.company = company;
                }
            });
        } else {
            this.companyService.getCompany(this.authService.getCurrentCompany()).subscribe(company => {
                this.company = company;
            });
        }

        const hiddenUrlsForNavigation = [
            `/company/${this.company?._id}/profiles`,
            '/manage-profiles-users/profiles',
        ];

        if (!hiddenUrlsForNavigation.includes(this.router.url)) {
            this.showNav.next(true);
        }
    }

    getProfiles() {
        this.profileService
        .getProfiles(this.company?._id)
        .pipe(
            tap((data) => {
                this.profiles = data.profiles;
                this.change.detectChanges();
            }),
            catchError((err) => {
                this.toastr.error(
                    this.translate.instant('company.profiles.error'),
                );
                return of(err);
            }),
            untilDestroyed(this),
        )
        .subscribe();
    }

    editProfile(profile) {
        this.showNav.next(true);
        this.profile.total = this.profiles.length;
        this.profile.type = profile.type;
        this.profileService.labels.push(...this.getCompanyServices())
        this.router.navigate([profile._id, profile.type, 'general'], {
            relativeTo: this.route,
        });
        this.profileService.setPreviousRoute(this.router.url);
    }

    private onRouteChange() {
        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                tap(() => this.getProfiles()),
                untilDestroyed(this),
            )
            .subscribe();
    }

    private getCompanyServices() {
        return this.serviceService.services.map(({ key }) => key)
            .filter((key) => this.company.services[key]);
    }
}
